import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { EBadgePosition } from '@appcharge/shared-ui';
import useBadges from 'api/useBadges';
import { useProduct } from 'api/useProduct';
import {
  Badge,
  BundleBadge,
  EBadgeType,
  Offer,
  RollingOfferProductSequence,
  RollingSequenceProduct
} from 'common/contracts';
import { MAX_USD_PRICE, MIN_USD_PRICE } from 'constants/constants';
import { EProductPriority, ESaleDiscountDisplayType } from 'constants/enums';
import { validateNumericField } from 'views/Offers/RollingOffers/Form/RollingOfferForm.utils';

import BarPointsForm from 'components/BarPointsForm/BarPointsForm';
import Checkbox from 'components/Checkbox/Checkbox';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Product } from 'components/ProductsForm/ProductsForm.types';

import CardProductsTable from './CardProductsTable';

interface CardContentProps {
  productSequenceItem: RollingOfferProductSequence;
  sequenceIndex: number;
  offerName: string;
  updateSubOfferData: (
    updatedData: Partial<RollingOfferProductSequence>,
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  setBarPointsValidity?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  relevantBadgeType?: EBadgeType;
  progressBarOffers?: Offer[];
}

const CardContent: React.FC<CardContentProps> = ({
  productSequenceItem,
  sequenceIndex,
  offerName,
  updateSubOfferData,
  setBarPointsValidity,
  relevantBadgeType = EBadgeType.EMBLEM,
  progressBarOffers
}) => {
  const { publisherId } = useParams();
  const { getBadges } = useBadges(publisherId);
  const { data: productsData } = useProduct(publisherId).getProducts;

  const [localQuantities, setLocalQuantities] = useState<
    Record<number, string>
  >({});
  const [badgeList, setBadgeList] = useState<BundleBadge[]>([]);

  const isSubOfferFree =
    productSequenceItem?.setAsFree ||
    productSequenceItem?.priceInUsdCents === 0;
  const shouldShowSaleFields = productSequenceItem?.products?.length === 1;

  const priceError = useMemo(() => {
    if (isSubOfferFree) return null;
    return validateNumericField(productSequenceItem?.priceInUsdCents, {
      min: MIN_USD_PRICE,
      max: MAX_USD_PRICE,
      fieldName: 'Price'
    });
  }, [isSubOfferFree, productSequenceItem?.priceInUsdCents]);

  const priceDiscountError = useMemo(() => {
    return validateNumericField(productSequenceItem?.priceDiscount?.discount, {
      min: 0,
      max: 99,
      fieldName: 'Price discount'
    });
  }, [productSequenceItem?.priceDiscount?.discount]);

  useEffect(() => {
    if (!getBadges.data?.result) return;
    const badgesDropdownItems = getBadges.data.result
      .filter((badge: Badge) => badge.type === relevantBadgeType)
      .map((badge: Badge) => ({
        content: badge.name,
        name: badge.name,
        key: badge.badgeId,
        value: badge.badgeId,
        label: badge.name,
        url:
          relevantBadgeType === EBadgeType.RIBBON
            ? badge.ribbon?.imageUrl
            : badge.emblem?.imageUrl,
        position: badge.position as EBadgePosition
      }));
    setBadgeList(badgesDropdownItems);
  }, [getBadges.data, relevantBadgeType]);

  const selectedBadge = useMemo(() => {
    const badgeId = productSequenceItem.badges?.[0]?.badge?.badgeId;
    if (!badgeId) return undefined;
    return badgeList.find((b) => b.value === badgeId);
  }, [productSequenceItem.badges, badgeList]);

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSubOfferData({ displayName: e.target.value }, e);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsedValue = Number(inputValue);
    const invalidPrice =
      !inputValue || isNaN(parsedValue) || parsedValue < MIN_USD_PRICE;
    const updatedData = invalidPrice
      ? { priceInUsdCents: undefined }
      : { priceInUsdCents: parsedValue, setAsFree: parsedValue === 0 };
    updateSubOfferData(updatedData, e);
  };

  const handleSetAsFreeChange = (checked: boolean) => {
    updateSubOfferData({
      setAsFree: checked,
      priceInUsdCents: checked
        ? 0
        : Math.max(
            MIN_USD_PRICE,
            productSequenceItem?.priceInUsdCents || MIN_USD_PRICE
          )
    });
  };
  const handleProductSaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value < 0) return;

    updateSubOfferData(
      {
        productSale: {
          sale: value,
          type: ESaleDiscountDisplayType.PERCENTAGE
        }
      },
      e
    );
  };

  const handlePriceDiscountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(e.target.value);
    if (value < 0) return;

    updateSubOfferData(
      {
        priceDiscount: {
          discount: value,
          type: ESaleDiscountDisplayType.PERCENTAGE
        }
      },
      e
    );
  };

  const handleBadgeChange = (badgeId: string) => {
    const currentBadge = getBadges?.data?.result?.find(
      (b: Badge) => b.badgeId === badgeId
    );
    if (currentBadge) {
      updateSubOfferData({
        badges: [{ badge: currentBadge }]
      });
    }
  };

  const handleProductChange = (
    productIndex: number,
    productId: string | null
  ) => {
    if (productIndex == null) return;
    if (!productId) {
      updateSubOfferData({
        products: productSequenceItem?.products?.map((product, index) =>
          index === productIndex
            ? { ...product, product: undefined, images: { product: undefined } }
            : product
        )
      });
      return;
    }

    const selectedProduct = productsData?.result?.find(
      (product: Product) => product.productId === productId
    );
    if (selectedProduct) {
      updateSubOfferData({
        products: productSequenceItem?.products?.map(
          (product: RollingSequenceProduct, index: number) =>
            index === productIndex
              ? {
                  ...product,
                  product: {
                    ...selectedProduct,
                    productId: selectedProduct.productId,
                    name: selectedProduct.name,
                    publisherProductId: selectedProduct.publisherProductId
                  },
                  images: { product: selectedProduct.images?.[0]?.url }
                }
              : product
        )
      });
    }
  };

  const handlePriorityChange = (
    productIndex: number,
    priority: EProductPriority
  ) => {
    updateSubOfferData({
      products: productSequenceItem?.products?.map(
        (product: RollingSequenceProduct, index: number) =>
          index === productIndex ? { ...product, priority } : product
      )
    });
  };

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    productIndex: number
  ) => {
    if (productIndex == null) return;
    const inputValue = e.target.value;
    const newQuantity = inputValue.length !== 0 ? Number(inputValue) : 0;

    if (newQuantity < 1 && inputValue !== '') return;

    updateSubOfferData({
      products: productSequenceItem?.products?.map(
        (product: RollingSequenceProduct, index: number) =>
          index === productIndex
            ? { ...product, quantity: newQuantity }
            : product
      )
    });
    setLocalQuantities((prev) => ({ ...prev, [productIndex]: inputValue }));
  };

  const handleRemoveProduct = (productIndex: number) => {
    if (productIndex == null) return;
    updateSubOfferData({
      products: productSequenceItem?.products?.filter(
        (_: RollingSequenceProduct, index: number) => index !== productIndex
      )
    });
  };

  const handleAddProduct = () => {
    updateSubOfferData({
      products: [
        ...(productSequenceItem?.products || []),
        { product: { images: [] }, priority: EProductPriority.SUB }
      ]
    });
  };

  const handleProgressBarChange = useCallback(
    (
      values: {
        progressBarId: string | null;
        progressBarPoints: number | null;
      },
      isValid: boolean
    ) => {
      const hasBarId =
        values.progressBarId !== null && values.progressBarId !== '';
      const hasPoints =
        values.progressBarPoints !== null && values.progressBarPoints > 0;
      const hasAnyValue = hasBarId || hasPoints;

      updateSubOfferData({
        progressBarPoints: hasAnyValue
          ? [
              {
                barId: values.progressBarId ?? '',
                points: values.progressBarPoints ?? 0
              }
            ]
          : undefined
      });

      if (setBarPointsValidity) {
        setBarPointsValidity((prev) => ({
          ...prev,
          [sequenceIndex]: isValid
        }));
      }
    },
    [updateSubOfferData, setBarPointsValidity, sequenceIndex]
  );

  const barPointsInitialValues = useMemo(() => {
    const barId = productSequenceItem?.progressBarPoints?.[0]?.barId;
    const points = productSequenceItem?.progressBarPoints?.[0]?.points;
    return {
      progressBarId: barId === '' ? null : barId ?? null,
      progressBarPoints: points ?? null
    };
  }, [productSequenceItem?.progressBarPoints]);

  return (
    <div className="flex flex-col gap-5 p-[22px]">
      <Input
        id={`display-name-${sequenceIndex}`}
        label="Display Name"
        name="displayName"
        value={productSequenceItem?.displayName || offerName}
        onChange={handleDisplayNameChange}
        isRequired
        placeholder="Display name"
      />

      <Input
        id={`price-${sequenceIndex}`}
        label="Price"
        name="price"
        value={
          isSubOfferFree
            ? '0'
            : `${productSequenceItem?.priceInUsdCents ?? ''}`
        }
        onChange={handlePriceChange}
        isRequired
        type="number"
        placeholder="0.8"
        min={0}
        disabled={isSubOfferFree}
        leftIcon={<span>$</span>}
        leftIconStyles={`${isSubOfferFree ? 'text-base-mutedForeground' : 'text-base-foreground'} text-p-s absolute left-3 top-1/2 -translate-y-1/2`}
        state={priceError ? 'error' : 'default'}
        description={
          priceError ||
          (!isSubOfferFree ? 'Price point is not localized' : undefined)
        }
      />

      <Checkbox
        id={`set-as-free-${sequenceIndex}`}
        label="Set as Free"
        checked={isSubOfferFree}
        onCheckedChange={handleSetAsFreeChange}
      />

      <BarPointsForm
        initialValues={barPointsInitialValues}
        onChange={handleProgressBarChange}
        progressBarOffers={progressBarOffers}
      />

      {shouldShowSaleFields && (
        <div className="grid grid-cols-3 gap-5">
          <Input
            id={`product-sale-${sequenceIndex}`}
            label="Main Product Sale"
            name="productSale"
            value={`${productSequenceItem?.productSale?.sale ?? ''}`}
            onChange={handleProductSaleChange}
            type="number"
            placeholder="00"
            min={0}
            leftIcon={<span>%</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
          />
          <Input
            id={`price-discount-${sequenceIndex}`}
            label="Price Discount"
            name="priceDiscount"
            value={`${productSequenceItem?.priceDiscount?.discount ?? ''}`}
            onChange={handlePriceDiscountChange}
            type="number"
            placeholder="00"
            min={0}
            leftIcon={<span>%</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
            state={priceDiscountError ? 'error' : 'default'}
            description={priceDiscountError || undefined}
          />
          <InputSelectDropdown
            id={`offer-badge-${sequenceIndex}`}
            label="Offer Badge"
            placeholder="Select"
            options={badgeList.map((b) => ({
              value: b.value,
              label: b.name || ''
            }))}
            defaultSelected={
              selectedBadge
                ? {
                    value: selectedBadge.value,
                    label: selectedBadge.name || ''
                  }
                : undefined
            }
            onOptionChange={handleBadgeChange}
            buttonClassName="h-10"
          />
        </div>
      )}

      <CardProductsTable
        productSequenceItem={productSequenceItem}
        sequenceIndex={sequenceIndex}
        onProductChange={handleProductChange}
        onPriorityChange={handlePriorityChange}
        onQuantityChange={handleQuantityChange}
        onRemoveProduct={handleRemoveProduct}
        onAddProduct={handleAddProduct}
        localQuantities={localQuantities}
      />
    </div>
  );
};

export default CardContent;
